import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface TimelineEntry {
  /** Godzina zdarzenia, np. „11:30”. */
  time: string;
  label: ReactNode;
  /** Wartosc po prawej stronie, np. wynik seta „6:3”. */
  value?: ReactNode;
  /** Wyroznia wpis (np. zakonczenie meczu). */
  highlighted?: boolean;
  icon?: ReactNode;
}

export interface TimelineProps {
  entries: readonly TimelineEntry[];
  className?: string;
}

/**
 * Os czasu zdarzen — sekcja „HISTORIA MECZU” z ekranu wprowadzania wyniku.
 */
export function Timeline({ entries, className }: TimelineProps) {
  return (
    <ol className={cn('flex flex-col', className)}>
      {entries.map((entry, index) => (
        <li
          key={index}
          className={cn(
            'border-border flex items-center gap-3 border-b py-2 last:border-b-0',
            entry.highlighted && 'text-success-700 font-semibold',
          )}
        >
          <span
            className={cn(
              'w-11 shrink-0 text-xs tabular-nums',
              entry.highlighted ? 'text-success-600' : 'text-fg-subtle',
            )}
          >
            {entry.time}
          </span>

          <span className="min-w-0 flex-1 truncate text-[0.8125rem]">{entry.label}</span>

          {entry.value !== undefined && (
            <span className="text-fg shrink-0 text-[0.8125rem] font-semibold tabular-nums">
              {entry.value}
            </span>
          )}

          {entry.icon && <span className="shrink-0 [&_svg]:size-4">{entry.icon}</span>}
        </li>
      ))}
    </ol>
  );
}

export interface StepItem {
  label: ReactNode;
  description?: ReactNode;
  state: 'done' | 'current' | 'todo';
  icon?: ReactNode;
}

export interface StepListProps {
  steps: readonly StepItem[];
  className?: string;
}

/**
 * Lista kolejnych krokow — panel „NASTĘPNE KROKI” z ekranu losowania.
 *
 * Krok biezacy jest wyroznniony kolorem marki, kroki przysze wyszarzone.
 */
export function StepList({ steps, className }: StepListProps) {
  return (
    <ol className={cn('flex flex-col gap-4', className)}>
      {steps.map((step, index) => (
        <li key={index} className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold',
              step.state === 'done' && 'bg-brand text-fg-on-brand',
              step.state === 'current' && 'bg-brand-subtle text-brand-text border-brand border',
              step.state === 'todo' && 'bg-surface-sunken text-fg-subtle',
            )}
          >
            {step.icon ?? index + 1}
          </span>

          <div className="min-w-0 pt-1">
            <p
              className={cn(
                'text-[0.8125rem]',
                step.state === 'todo' ? 'text-fg-subtle' : 'text-fg font-medium',
              )}
            >
              {step.label}
            </p>
            {step.description && <p className="text-fg-subtle text-xs">{step.description}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export interface NotificationEntry {
  id: string;
  message: ReactNode;
  time: string;
  tone?: 'info' | 'success' | 'warning';
  icon?: ReactNode;
}

export interface NotificationListProps {
  entries: readonly NotificationEntry[];
  className?: string;
}

const notificationToneClasses = {
  info: 'bg-info-50 text-info-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
} as const;

/** Lista powiadomien — panel „POWIADOMIENIA” z centrum turnieju. */
export function NotificationList({ entries, className }: NotificationListProps) {
  return (
    <ul className={cn('flex flex-col', className)}>
      {entries.map((entry) => (
        <li key={entry.id} className="border-border flex items-center gap-3 border-b py-2.5 last:border-b-0">
          <span
            className={cn(
              'grid size-7 shrink-0 place-items-center rounded-full [&_svg]:size-4',
              notificationToneClasses[entry.tone ?? 'info'],
            )}
          >
            {entry.icon}
          </span>
          <span className="text-fg min-w-0 flex-1 text-[0.8125rem]">{entry.message}</span>
          <span className="text-fg-subtle shrink-0 text-xs tabular-nums">{entry.time}</span>
        </li>
      ))}
    </ul>
  );
}
